import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { api } from "@/lib/api";
import { extractError } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

const CURRENCY_OPTIONS = [
  { value: "INR", label: "INR - ₹" },
  { value: "USD", label: "USD - $" },
  { value: "AUD", label: "AUD - A$" },
  { value: "EUR", label: "EUR - €" },
  { value: "GBP", label: "GBP - £" },
];
const EMPTY = { name: "", code: "", duration_days: 30, price: 0, currency: "INR", pricing: [{ currency: "INR", price: 0 }], description: "", status: "active", display_order: 0 };

export default function SuperPlans() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    const { data } = await api.get("/super/plans");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, duration_days: Number(form.duration_days), price: Number(form.price), display_order: Number(form.display_order) };
      if (editing) await api.patch(`/super/plans/${editing}`, payload);
      else await api.post("/super/plans", payload);
      toast.success("Saved");
      setOpen(false); setEditing(null); setForm(EMPTY); load();
    } catch (err) { toast.error(extractError(err)); }
  };

  const deactivate = async (id) => {
    if (!confirm("Deactivate this plan?")) return;
    await api.delete(`/super/plans/${id}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="super-plans-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-zinc-500">Catalog</div>
          <h1 className="font-serif text-4xl tracking-tighter mt-1 text-white">Plans</h1>
          <p className="text-sm text-zinc-500 mt-1">Subscription plans offered to all tenants.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(EMPTY); setEditing(null); } }}>
          <DialogTrigger asChild>
            <Button className="bg-white text-black hover:bg-zinc-200 rounded-full" data-testid="super-plan-add-btn"><Plus size={16} className="mr-1" /> Add plan</Button>
          </DialogTrigger>
          <DialogContent className="bg-[#18181B] border-white/10 text-zinc-200">
            <DialogHeader><DialogTitle>{editing ? "Edit plan" : "New plan"}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3" data-testid="super-plan-form">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Name</Label><Input className="bg-[#0a0a0c] border-white/10" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="super-plan-name" /></div>
                <div><Label>Code</Label><Input className="bg-[#0a0a0c] border-white/10" required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="super-plan-code" /></div>
                <div><Label>Duration (days)</Label><Input className="bg-[#0a0a0c] border-white/10" type="number" value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: e.target.value })} /></div>
                <div><Label>Base price</Label><Input className="bg-[#0a0a0c] border-white/10" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></div>
                <div><Label>Default currency</Label><Select value={form.currency} onValueChange={(value) => setForm({ ...form, currency: value })}>
                  <SelectTrigger className="mt-1.5 h-11"><SelectValue placeholder="Select currency" /></SelectTrigger>
                  <SelectContent>
                    {CURRENCY_OPTIONS.map((item) => (
                      <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select></div>
                <div><Label>Order</Label><Input className="bg-[#0a0a0c] border-white/10" type="number" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: e.target.value })} /></div>
              </div>
              <div><Label>Description</Label><Textarea className="bg-[#0a0a0c] border-white/10" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Pricing by currency</Label>
                  <Button type="button" variant="outline" onClick={() => setForm({ ...form, pricing: [...(form.pricing || []), { currency: "INR", price: 0 }] })}>Add price</Button>
                </div>
                {(form.pricing || []).map((priceRow, idx) => (
                  <div key={idx} className="grid grid-cols-3 gap-3 items-end">
                    <div><Label>Currency</Label><Select value={priceRow.currency} onValueChange={(value) => {
                      const pricing = [...(form.pricing || [])];
                      pricing[idx] = { ...pricing[idx], currency: value };
                      setForm({ ...form, pricing });
                    }}>
                      <SelectTrigger className="mt-1.5 h-11"><SelectValue placeholder="Currency" /></SelectTrigger>
                      <SelectContent>
                        {CURRENCY_OPTIONS.map((item) => (
                          <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select></div>
                    <div><Label>Price</Label><Input className="bg-[#0a0a0c] border-white/10" type="number" value={priceRow.price} onChange={(e) => {
                      const pricing = [...(form.pricing || [])];
                      pricing[idx] = { ...pricing[idx], price: Number(e.target.value) };
                      setForm({ ...form, pricing });
                    }} /></div>
                    <div className="flex items-center gap-2">
                      <Button type="button" variant="ghost" className="text-red-400" onClick={() => {
                        const pricing = [...(form.pricing || [])];
                        pricing.splice(idx, 1);
                        setForm({ ...form, pricing });
                      }}>Remove</Button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.status === "active"} onCheckedChange={(v) => setForm({ ...form, status: v ? "active" : "inactive" })} />
                <span className="text-sm">Active</span>
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-[#D85C38] hover:bg-[#B84A2A] text-white" data-testid="super-plan-save">{editing ? "Save" : "Create"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-[#18181B] border border-white/5 rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/5"><TableHead className="text-zinc-400">Name</TableHead><TableHead className="text-zinc-400">Duration</TableHead><TableHead className="text-zinc-400">Price</TableHead><TableHead className="text-zinc-400">Active subs</TableHead><TableHead className="text-zinc-400">Status</TableHead><TableHead className="text-zinc-400 text-right">Action</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {items.map((p) => (
              <TableRow key={p.id} className="border-white/5 hover:bg-white/5" data-testid={`super-plan-row-${p.id}`}>
                <TableCell className="text-zinc-200">{p.name}<div className="text-xs text-zinc-500">{p.code}</div></TableCell>
                <TableCell className="text-zinc-300">{p.duration_days} days</TableCell>
                <TableCell className="text-zinc-300">{p.currency} {p.price?.toLocaleString()}</TableCell>
                <TableCell className="text-zinc-300">{p.active_subscriptions}</TableCell>
                <TableCell><Badge variant="outline" className={p.status === "active" ? "bg-emerald-900/40 text-emerald-300 border-emerald-800" : "bg-zinc-800 text-zinc-400 border-zinc-700"}>{p.status}</Badge></TableCell>
                <TableCell className="text-right space-x-2">
                  <Button variant="ghost" size="sm" className="text-zinc-300 hover:bg-white/10" onClick={() => { setForm(p); setEditing(p.id); setOpen(true); }} data-testid={`super-plan-edit-${p.id}`}>Edit</Button>
                  <Button variant="ghost" size="sm" className="text-red-400 hover:bg-red-900/20" onClick={() => deactivate(p.id)} data-testid={`super-plan-deactivate-${p.id}`}>Deactivate</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
